import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import { useChatStore } from './chatStore'
import { supabase } from '../lib/supabase'

interface User {
  id: string
  email: string
  name?: string
  fullName?: string
  onboardingCompleted?: boolean
}

interface AuthState {
  user: User | null
  token: string | null
  isAuthenticated: boolean
  login: (token: string, user: User) => void
  logout: () => Promise<void>
  setUser: (user: User) => void
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      token: null,
      isAuthenticated: false,
      
      login: (token: string, user: User) => {
        console.log('Auth store login:', { user: user.email, hasToken: !!token })
        
        // Clear chat history for new user
        useChatStore.getState().reset()
        // Also clear from localStorage directly
        localStorage.removeItem('Memtrail-chat')
        console.log('Chat history cleared for new user')
        
        set({ 
          token, 
          user, 
          isAuthenticated: true 
        })
        
        // Store in both localStorage and send message to extension
        try {
          localStorage.setItem('kuraAuthToken', token);
          localStorage.setItem('kuraUser', JSON.stringify(user));
          console.log('Auth stored in localStorage');
          
          // Send message to extension via custom event
          console.log('Dispatching auth event for extension...');
          window.dispatchEvent(new CustomEvent('kuraAuthUpdate', {
            detail: { token, user }
          }));
          
          // Try to send to extension - try multiple extension IDs
          if (typeof window !== 'undefined' && (window as any).chrome?.runtime) {
            console.log('Attempting to send auth to extension...');
            
            // Try known extension IDs (add your local extension ID here)
            const extensionIds = [
              'coloknlifhjbifmkgimoadlnimelgnkd', // Production ID
              // Local development IDs vary - we'll use a fallback approach
            ];
            
            // Try each extension ID (using .then() instead of await since login is not async)
            for (const extId of extensionIds) {
              (window as any).chrome.runtime.sendMessage(extId, {
                action: 'setAuth',
                token: token,
                user: user
              }).then(() => {
                console.log('Auth sent to extension:', extId);
              }).catch(() => {
                console.log('Extension not found:', extId);
              });
            }
          } else {
            console.log('Chrome runtime not available');
          }
        } catch (error) {
          console.error('Failed to store auth:', error);
        }
      },
      
      logout: async () => {
        console.log('[Logout] Starting logout process...')
        
        // Check if this logout was initiated by webapp (token still exists)
        // or by extension (token already cleared by contentScript)
        const webappInitiated = localStorage.getItem('kuraAuthToken') !== null;
        console.log('[Logout] Initiated by:', webappInitiated ? 'webapp' : 'extension');
        
        // CRITICAL: Sign out from Supabase first to clear the session
        if (supabase) {
          try {
            console.log('[Logout] Signing out from Supabase...')
            const { error } = await supabase.auth.signOut()
            if (error) {
              console.error('[Logout] Supabase signOut error:', error)
            } else {
              console.log('[Logout] Supabase session cleared successfully')
            }
          } catch (error) {
            console.error('[Logout] Exception during Supabase signOut:', error)
          }
        } else {
          console.warn('[Logout] Supabase client not available')
        }
        
        // Clear chat history on logout
        useChatStore.getState().reset()
        // Also clear from localStorage directly
        localStorage.removeItem('Memtrail-chat')
        console.log('[Logout] Chat history cleared')
        
        // Clear auth state
        set({ 
          token: null, 
          user: null, 
          isAuthenticated: false 
        })
        console.log('[Logout] Auth state cleared')
        
        // Clear localStorage
        try {
          localStorage.removeItem('kuraAuthToken');
          localStorage.removeItem('kuraUser');
          console.log('[Logout] Auth cleared from localStorage');
          
          // Clear dismissed notifications from sessionStorage
          sessionStorage.removeItem('Memtrail_dismissed_notifications');
          console.log('[Logout] Dismissed notifications cleared from sessionStorage');
          
          // Dispatch logout event for extension ONLY if webapp initiated the logout
          // (If extension initiated, it already knows - don't create a loop)
          if (webappInitiated) {
            window.dispatchEvent(new CustomEvent('kuraAuthUpdate', {
              detail: { token: null, user: null }
            }));
            console.log('[Logout] Logout event dispatched for extension');
          } else {
            console.log('[Logout] Skipping extension event (extension initiated logout)');
          }
        } catch (error) {
          console.error('[Logout] Failed to clear auth from localStorage:', error);
        }
        
        console.log('[Logout] Logout complete')
      },
      
      setUser: (user: User) => {
        const currentUser = get().user
        
        // If user changed, clear chat history
        if (currentUser && currentUser.id !== user.id) {
          useChatStore.getState().reset()
          // Also clear from localStorage directly
          localStorage.removeItem('Memtrail-chat')
          console.log('User changed, chat history cleared')
        }
        
        set({ user })
        
        // Update localStorage with new user data
        try {
          localStorage.setItem('kuraUser', JSON.stringify(user));
          console.log('User updated in localStorage:', user);
        } catch (error) {
          console.error('Failed to update user in localStorage:', error);
        }
      },
    }),
    {
      name: 'kura-auth',
      partialize: (state) => ({ 
        token: state.token, 
        user: state.user,
        isAuthenticated: state.isAuthenticated 
      }),
    }
  )
)